<template>
  <v-card class="v-card--bordered radius-5 elevation-1">
    <!--<editor-fold description="Automatic markup settings">-->
    <v-row
      class="list--bordered grey--text text--darken-1 px-6 mx-0"
      align="center"
      dense
    >
      <v-col cols="auto">
        Apply
      </v-col>
      <v-col>
        <v-text-field
          v-model="automaticMarkup.percentage"
          outlined
          class="v-text-field--no-border body-1"
          placeholder="0"
          type="number"
          flat
          suffix="%"
          hide-details
          single-line
          dense
        />
      </v-col>
      <v-col>
        <v-select
          v-model="automaticMarkup.type"
          outlined
          class="v-text-field--no-border body-1"
          single-line
          hide-details
          :items="[ { value: 'margin', text: 'Margin' }, { value: 'addon', text: 'Addon' } ]"
          dense
        />
      </v-col>
      <v-col cols="auto">
        upon changing
      </v-col>
      <v-col cols="auto">
        <v-switch
          v-model="automaticMarkup.purchaseActive"
          hide-details
          single-line
          class="pa-0 ma-0"
        />
      </v-col>
      <v-col cols="auto">
        Purchase price
      </v-col>
      <v-col cols="auto">
        <v-switch
          v-model="automaticMarkup.saleActive"
          hide-details
          single-line
          class="pa-0 ma-0"
        />
      </v-col>
      <v-col cols="auto">
        Sale price
      </v-col>
      <v-spacer />
      <v-col cols="auto">
        <v-btn
          small
          rounded
          text
          color="grey darken-3"
          class="text-none"
          @click="applyMarkupToAll()"
        >
          Apply to all
        </v-btn>
      </v-col>
    </v-row>
    <!--</editor-fold>-->
    <draggable
      v-model="itemsDirty"
      tag="div"
      handle=".sortHandle"
      element="div"
    >
      <item-list-row
        v-for="(item, index) in itemsDirty"
        :key="'item_' + index"
        :item.sync="item"
        :index="index"
        :deletable="index > 0"
        :currency="currency"
        :automatic-markup-percentage="Number(automaticMarkup.percentage)"
        :automatic-markup-type="automaticMarkup.type"
        :automatic-markup-purchase="automaticMarkup.purchaseActive"
        :automatic-markup-sale="automaticMarkup.saleActive"
        :automatic-markup-apply="automaticMarkup.apply"
        @item:remove="removeItem(index)"
        @item:add="addItem"
        @calculate:sale-price="calculateTotal()"
        @calculate:purchase-price="calculateTotal()"
      />
    </draggable>
    <div class="equipment-items__item pl-6">
      <v-btn
        text
        color="primary"
        @click.stop="addItem"
      >
        <v-icon left>
          add
        </v-icon>
        Add New Item
      </v-btn>
    </div>
    <div class="equipment-items__item--total">
      <v-row
        dense
        align="center"
      >
        <v-col class="px-6">
          <div
            v-if="purchasePriceDiffers || salePriceDiffers"
            class="error--text caption"
          >
            Total values manually overriden.
            <a @click.prevent="calculateTotal">
              Calculate
            </a>
          </div>
          <v-menu
            v-else
            open-on-hover
          >
            <template v-slot:activator="{ on }">
              <span
                class="grey--text caption"
                v-on="on"
              >
                Shortcuts
              </span>
            </template>
            <div class="white radius-5 pa-2 grey--text text--darken-1">
              <strong>Tab:</strong> Navigate<br>
              <strong>Ctrl+Enter:</strong> Add New Item<br>
              <strong>Ctrl+Del:</strong> Delete Item
            </div>
          </v-menu>
        </v-col>
        <v-col
          cols="auto"
          class="body-2 px-6"
        >
          TOTAL
        </v-col>
        <v-col cols="auto">
          <validation-provider
            v-slot="v"
            rules="required"
            name="Purchase Price"
          >
            <currency-input
              v-model="purchasePriceDirty"
              :suffix="currency"
              :hide-details="!v.errors.length"
              class="v-text-field--light-suffix"
              placeholder="Purchase"
              style="width: 140px"
              solo
              flat
              :error-messages="v.errors"
            />
          </validation-provider>
        </v-col>
        <v-col cols="auto">
          <validation-provider
            v-slot="v"
            rules="required"
            name="Sale Price"
          >
            <currency-input
              v-model="salePriceDirty"
              :suffix="currency"
              :hide-details="!v.errors.length"
              class="v-text-field--light-suffix"
              placeholder="Sale"
              style="width: 140px"
              solo
              flat
              :error-messages="v.errors"
            />
          </validation-provider>
        </v-col>
        <v-col
          cols="auto"
          style="min-width: 32px"
        />
      </v-row>
    </div>
  </v-card>
</template>

<script>
import { cloneDeep } from 'lodash'
import CurrencyInput from '@/components/helpers/CurrencyInput'
import YachtEquipmentPack from '@/models/YachtEquipmentPack'
import ItemListRow from '@/components/settings/yachtEquipmentPacks/ItemListRow'
import draggable from 'vuedraggable'

export default {
    name: 'ItemListTable',
    components: {
      ItemListRow,
      CurrencyInput,
      draggable
    },
    props: {
      items: {
        type: Array,
        default: () => YachtEquipmentPack.emptyModel.items
      },
      purchasePrice: {
        type: Number,
        default: YachtEquipmentPack.emptyModel.purchase_price
      },
      salePrice: {
        type: Number,
        default: YachtEquipmentPack.emptyModel.sale_price
      },
      currency: {
        type: String,
        default: YachtEquipmentPack.emptyModel.currency
      }
    },
    data() {
      return {
        automaticMarkup: {
          percentage: 0,
          type: 'margin', // margin or addon
          purchaseActive: false,
          saleActive: false,
          apply: false
        }
      }
    },
    computed: {
      itemsDirty: {
        get: function () {
          return this.items
        },
        set: function (val) {
          this.$emit('update:items', val)
        }
      },
      purchasePriceDirty: {
        get: function () {
          return this.purchasePrice
        },
        set: function (val) {
          this.$emit('update:purchasePrice', val)
        }
      },
      salePriceDirty: {
        get: function () {
          return this.salePrice
        },
        set: function (val) {
          this.$emit('update:salePrice', val)
        }
      },
      totalPurchasePrice() {
        let values = this.itemsDirty
          .map(item => Number(item.purchase_price))
          .filter(value => !!value && value > 0)

        if (values.length === 0) return null

        return values.reduce((a, b) => Number(a) + Number(b))
      },
      totalSalePrice() {
        let values = this.itemsDirty
          .map(item => Number(item.sale_price))
          .filter(value => !!value && value > 0)

        if (values.length === 0) return null

        return values.reduce((a, b) => Number(a) + Number(b))
      },
      purchasePriceDiffers() {
        return Number(this.totalPurchasePrice).toFixed(2) !== Number(this.purchasePriceDirty).toFixed(2)
      },
      salePriceDiffers() {
        return Number(this.totalSalePrice).toFixed(2) !== Number(this.salePriceDirty).toFixed(2)
      }
    },
    methods: {
      addItem() {
        this.itemsDirty.push(cloneDeep(YachtEquipmentPack.emptyItem))
        this.focusLastItem()
      },
      removeItem(index) {
        this.itemsDirty.splice(index, 1)

        this.calculateTotal()
      },
      calculateTotal() {
        this.purchasePriceDirty = this.totalPurchasePrice
        this.salePriceDirty = this.totalSalePrice
      },
      focusLastItem() {
        this.$nextTick(() => {
          this.$el.querySelector(`[name='items_label_${this.itemsDirty.length - 1}']`).focus()
        })
      },
      focusItemOnIndex(index) {
        this.$nextTick(() => {
          this.$el.querySelector(`[name='items_label_${index}']`).focus()
        })
      },
      applyMarkupToAll() {
        this.automaticMarkup.apply = true

        this.$nextTick(() => {
          this.automaticMarkup.apply = false
        })
      }
    }
  }
</script>

<style lang="stylus">
  .equipment-items__item
    border-bottom: 1px solid #eeeeee
    background: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1)

    &:last-child
      border-bottom: none
    &--total
      border-top: 1px solid #c3c3c3
    &:hover
      background: rgba(0, 0, 0, .08) !important

    .v-input__slot
      background: transparent !important

  .v-text-field--light-suffix
    .v-text-field__suffix
      opacity: 0.4
      font-size: 13px

    &.v-text-field--small
      .v-text-field__suffix
        font-size: 12px
</style>
